import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { affiliateService } from '../../services/affiliateService';

interface Payout {
  id: string;
  affiliate_id: string;
  affiliate_name: string;
  affiliate_email: string;
  amount: number;
  currency: string;
  status: 'pending' | 'processing' | 'paid' | 'failed';
  payment_method: string;
  payment_details: string;
  notes: string;
  created_at: string;
  paid_at: string | null;
  program_id?: string;
}

interface PayoutsManagementProps {
  programId: string;
}

const PayoutsManagement: React.FC<PayoutsManagementProps> = ({ programId }) => {
  const [payouts, setPayouts] = useState<Payout[]>([]);
  const [filtered, setFiltered] = useState<Payout[]>([]);
  const [selectedPayout, setSelectedPayout] = useState<Payout | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [showModal, setShowModal] = useState<boolean>(false);
  const [creating, setCreating] = useState<boolean>(false);
  const [linking, setLinking] = useState<boolean>(false);
  const [exporting, setExporting] = useState<boolean>(false);
  const [exportStatus, setExportStatus] = useState<'pending' | 'processing'>('processing');
  // Filters
  const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'processing' | 'paid' | 'failed'>('all');
  const [search, setSearch] = useState<string>('');
  const [minAmount, setMinAmount] = useState<string>('');
  const [maxAmount, setMaxAmount] = useState<string>('');
  // Selection
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [createForm, setCreateForm] = useState<{ affiliate_id: string; amount: string; currency: string; notes?: string; paypal_email?: string }>({ affiliate_id: '', amount: '', currency: 'USD' });
  const [linkForm, setLinkForm] = useState<{ affiliate_id: string; paypal_email: string }>({ affiliate_id: '', paypal_email: '' });

  // Fetch payouts for the program
  useEffect(() => {
    const fetchPayouts = async () => {
      if (!programId) return;

      setLoading(true);
      setError('');
      try {
        const data = await affiliateService.listPayouts(programId);
        setPayouts(Array.isArray(data?.payouts) ? (data.payouts as any) : []);
      } catch (err: any) {
        console.error('Error fetching payouts:', err);
        setError(err?.message || 'Failed to load payouts. Please try again.');
        toast.error('Failed to load payouts');
      } finally {
        setLoading(false);
      }
    };

    fetchPayouts();
  }, [programId]);

  // Update payout status
  const handleStatusChange = async (payoutId: string, newStatus: 'pending' | 'processing' | 'paid' | 'failed') => {
    try {
      await affiliateService.updatePayoutStatus(programId, payoutId, newStatus);
      const data = await affiliateService.listPayouts(programId);
      setPayouts(Array.isArray(data?.payouts) ? (data.payouts as any) : []);
      toast.success(`Payout status updated to ${newStatus}`);
    } catch (err: any) {
      console.error('Error updating payout status:', err);
      toast.error(err?.message || 'Failed to update payout status');
    }
  };

  // Create a new payout
  const handleCreatePayout = async () => {
    try {
      if (!createForm.affiliate_id) {
        toast.error('Affiliate ID is required');
        return;
      }
      const amount = Number(createForm.amount);
      if (!Number.isFinite(amount) || amount <= 0) {
        toast.error('Enter a valid amount');
        return;
      }
      setCreating(true);
      const result = await affiliateService.createPayout(programId, {
        affiliate_id: createForm.affiliate_id,
        amount,
        currency: createForm.currency || 'USD',
        notes: createForm.notes,
        payment_method: 'paypal',
      });
      toast.success('Payout created');
      // refresh list
      const data = await affiliateService.listPayouts(programId);
      setPayouts(Array.isArray(data?.payouts) ? (data.payouts as any) : []);
      setShowModal(false);
      setCreateForm({ affiliate_id: '', amount: '', currency: 'USD' });
    } catch (err) {
      console.error('Error creating payout:', err);
      toast.error('Failed to create payout');
    } finally {
      setCreating(false);
    }
  };

  // Process multiple payouts
  const handleProcessPayouts = async (payoutIds: string[], status: 'pending' | 'processing' | 'paid' | 'cancelled' = 'processing') => {
    try {
      await affiliateService.processPayouts(programId, payoutIds, status);
      const data = await affiliateService.listPayouts(programId);
      setPayouts(Array.isArray(data?.payouts) ? (data.payouts as any) : []);
      toast.success(`${payoutIds.length} payouts processed successfully`);
    } catch (err: any) {
      console.error('Error processing payouts:', err);
      toast.error(err?.message || 'Failed to process payouts');
    }
  };

  // Apply filters whenever dependencies change
  useEffect(() => {
    let items = [...payouts];
    if (statusFilter !== 'all') items = items.filter(p => p.status === statusFilter);
    if (search.trim()) {
      const q = search.toLowerCase();
      items = items.filter(p =>
        (p.affiliate_name || '').toLowerCase().includes(q) ||
        (p.affiliate_email || '').toLowerCase().includes(q) ||
        (p.affiliate_id || '').toLowerCase().includes(q)
      );
    }
    const min = minAmount ? Number(minAmount) : undefined;
    const max = maxAmount ? Number(maxAmount) : undefined;
    if (min != null && !Number.isNaN(min)) items = items.filter(p => p.amount >= min);
    if (max != null && !Number.isNaN(max)) items = items.filter(p => p.amount <= max);
    setFiltered(items);
  }, [payouts, statusFilter, search, minAmount, maxAmount]);

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleSelectAll = () => {
    setSelectedIds(prev => {
      if (filtered.length && prev.size !== filtered.length) {
        return new Set(filtered.map(p => p.id));
      }
      return new Set();
    });
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-UK', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-UK', {
      style: 'currency',
      currency: currency || 'GBP'
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 p-4 rounded-md">
        <div className="flex items-center">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">{error}</h3>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white shadow overflow-hidden sm:rounded-md">
      <div className="px-4 py-5 sm:px-6 flex justify-between items-center">
        <h3 className="text-lg leading-6 font-medium text-gray-900">Affiliate Payouts</h3>
        <div className="flex gap-2">
          <div className="flex items-center gap-2">
            <select
              value={exportStatus}
              onChange={(e) => setExportStatus(e.target.value as any)}
              className="border rounded px-2 py-1 text-sm"
              title="Select status to export"
            >
              <option value="processing">Processing</option>
              <option value="pending">Pending</option>
            </select>
            <button
              onClick={async () => {
                try {
                  setExporting(true);
                  const blob = await affiliateService.exportPayoutsCsv(programId, exportStatus);
                  const url = window.URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  const now = new Date().toISOString().slice(0,10);
                  a.download = `payouts_${programId}_${exportStatus}_${now}.csv`;
                  document.body.appendChild(a);
                  a.click();
                  a.remove();
                  window.URL.revokeObjectURL(url);
                  toast.success('CSV exported');
                } catch (e: any) {
                  console.error(e);
                  toast.error(e?.message || 'Failed to export CSV');
                } finally {
                  setExporting(false);
                }
              }}
              disabled={exporting}
              className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-60"
            >
              {exporting ? 'Exporting…' : 'Export CSV'}
            </button>
          </div>
          <button
            onClick={() => setShowModal(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Create Payout
          </button>
        </div>
      </div>

      {/* Filters and Bulk Actions */}
      <div className="px-4 sm:px-6 pb-4 flex flex-col gap-3">
        <div className="flex flex-wrap gap-3 items-end">
          <div>
            <label htmlFor="status-filter" className="block text-xs text-gray-600 mb-1">Status</label>
            <select id="status-filter" className="border rounded px-2 py-2" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as any)}>
              <option value="all">All</option>
              <option value="pending">Pending</option>
              <option value="processing">Processing</option>
              <option value="paid">Paid</option>
              <option value="failed">Failed</option>
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Search</label>
            <input className="border rounded px-3 py-2" placeholder="Name / Email / ID" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Min Amount</label>
            <input className="border rounded px-3 py-2 w-32" type="number" min="0" step="0.01" value={minAmount} onChange={(e) => setMinAmount(e.target.value)} />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Max Amount</label>
            <input className="border rounded px-3 py-2 w-32" type="number" min="0" step="0.01" value={maxAmount} onChange={(e) => setMaxAmount(e.target.value)} />
          </div>
          <div className="ml-auto flex gap-2">
            <button
              onClick={() => handleProcessPayouts(Array.from(selectedIds), 'processing')}
              disabled={!selectedIds.size}
              className="px-3 py-2 text-sm rounded border disabled:opacity-50 hover:bg-gray-50"
              title="Move selected to Processing"
            >
              Bulk Process
            </button>
            <button
              onClick={() => handleProcessPayouts(Array.from(selectedIds), 'paid')}
              disabled={!selectedIds.size}
              className="px-3 py-2 text-sm rounded border disabled:opacity-50 hover:bg-gray-50"
              title="Mark selected as Paid"
            >
              Bulk Mark Paid
            </button>
            <button
              onClick={() => setSelectedIds(new Set())}
              disabled={!selectedIds.size}
              className="px-3 py-2 text-sm rounded border disabled:opacity-50 hover:bg-gray-50"
            >
              Clear Selection
            </button>
          </div>
        </div>
        <p className="text-xs text-gray-500">{selectedIds.size} selected</p>
      </div>

      {/* Link PayPal payout method */}
      <div className="px-4 sm:px-6 pb-4">
        <div className="bg-gray-50 border rounded p-3 flex flex-col md:flex-row gap-3 items-start md:items-end">
          <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-3 w-full">
            <input className="border rounded px-3 py-2 w-full" placeholder="Affiliate ID" value={linkForm.affiliate_id} onChange={(e) => setLinkForm((f) => ({ ...f, affiliate_id: e.target.value }))} />
            <input className="border rounded px-3 py-2 w-full" placeholder="PayPal Email" type="email" value={linkForm.paypal_email} onChange={(e) => setLinkForm((f) => ({ ...f, paypal_email: e.target.value }))} />
            <button
              onClick={async () => {
                try {
                  if (!linkForm.affiliate_id || !linkForm.paypal_email) { toast.error('Affiliate ID and PayPal email required'); return; }
                  setLinking(true);
                  await affiliateService.linkPayPalPayoutMethod(programId, linkForm.affiliate_id, linkForm.paypal_email);
                  toast.success('PayPal linked');
                  setLinkForm({ affiliate_id: '', paypal_email: '' });
                } catch (e) {
                  console.error(e);
                  toast.error('Failed to link PayPal');
                } finally {
                  setLinking(false);
                }
              }}
              disabled={linking}
              className="bg-indigo-600 hover:bg-indigo-700 text-white font-medium py-2 px-3 rounded disabled:opacity-60"
            >
              {linking ? 'Linking…' : 'Link PayPal'}
            </button>
          </div>
          <p className="text-xs text-gray-500">Ensure the affiliate has a valid tax status before payouts.</p>
        </div>
      </div>
      
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3">
                <input
                  type="checkbox"
                  aria-label="Select all"
                  checked={filtered.length > 0 && selectedIds.size === filtered.length}
                  onChange={toggleSelectAll}
                />
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Affiliate</th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Payment Method</th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date Created</th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date Paid</th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-6 py-4 text-center text-sm text-gray-500">
                  No payouts found
                </td>
              </tr>
            ) : (
              filtered.map((payout) => (
                <tr key={payout.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <input type="checkbox" aria-label={`Select ${payout.affiliate_id}`} checked={selectedIds.has(payout.id)} onChange={() => toggleSelect(payout.id)} />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{payout.affiliate_name}</div>
                    <div className="text-sm text-gray-500">{payout.affiliate_email}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatCurrency(payout.amount, payout.currency)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${payout.status === 'paid' ? 'bg-green-100 text-green-800' : payout.status === 'pending' ? 'bg-yellow-100 text-yellow-800' : payout.status === 'processing' ? 'bg-blue-100 text-blue-800' : 'bg-red-100 text-red-800'}`}>
                      {payout.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <div className="text-sm text-gray-900">{payout.payment_method}</div>
                    <div className="text-xs text-gray-500 truncate max-w-xs">{payout.payment_details}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(payout.created_at)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {payout.paid_at ? formatDate(payout.paid_at) : 'Not paid yet'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex space-x-2">
                      {payout.status === 'pending' && (
                        <button
                          onClick={() => handleStatusChange(payout.id, 'processing')}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          Process
                        </button>
                      )}
                      {payout.status === 'processing' && (
                        <button
                          onClick={() => handleStatusChange(payout.id, 'paid')}
                          className="text-green-600 hover:text-green-900"
                        >
                          Mark Paid
                        </button>
                      )}
                      <button
                        onClick={() => {
                          setSelectedPayout(payout);
                          // Open modal or view details
                        }}
                        className="text-indigo-600 hover:text-indigo-900"
                      >
                        View
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      
      {/* Create Payout Modal */}
      {showModal && (
        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                    <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title">
                      Create New Payout
                    </h3>
                    <div className="mt-4 grid grid-cols-1 gap-3">
                      <input className="border rounded px-3 py-2" placeholder="Affiliate ID" value={createForm.affiliate_id} onChange={(e) => setCreateForm((f) => ({ ...f, affiliate_id: e.target.value }))} />
                      <input className="border rounded px-3 py-2" placeholder="Amount" type="number" min="0" step="0.01" value={createForm.amount} onChange={(e) => setCreateForm((f) => ({ ...f, amount: e.target.value }))} />
                      <div className="grid grid-cols-2 gap-3">
                        <input className="border rounded px-3 py-2" placeholder="Currency (e.g., USD)" value={createForm.currency} onChange={(e) => setCreateForm((f) => ({ ...f, currency: e.target.value.toUpperCase() }))} />
                        <input className="border rounded px-3 py-2" placeholder="PayPal Email (optional)" type="email" value={createForm.paypal_email || ''} onChange={(e) => setCreateForm((f) => ({ ...f, paypal_email: e.target.value }))} />
                      </div>
                      <textarea className="border rounded px-3 py-2" placeholder="Notes (optional)" value={createForm.notes || ''} onChange={(e) => setCreateForm((f) => ({ ...f, notes: e.target.value }))} />
                      <p className="text-xs text-gray-500">Payment method: PayPal. Ensure affiliate has linked PayPal and has compliant tax status.</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button 
                  type="button" 
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={handleCreatePayout}
                  disabled={creating}
                >
                  {creating ? 'Creating…' : 'Create'}
                </button>
                <button 
                  type="button" 
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={() => setShowModal(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PayoutsManagement;
